import { useQuery } from "react-query";
import { useQueryClient } from "react-query";
import { useMutation } from "react-query";
import { useParams } from "react-router-dom";

import { userCheck } from "utils/request/auth";
import {
    deleteSpaceMember,
    getSpace,
    getSpaceMember,
    patchSpace,
    patchSpaceMember,
    postSpaceMemberInvite,
    postSpaceMemberInviteAccept,
    postSpaceMemberInviteReject,
} from "utils/request/space";
import { isValidUUID } from "utils/uuid";

export const useUser = (options = {}) => {
    const { space_uuid } = useParams();

    // URL 의 space_uuid 가 유효하면 백엔드에서 user.current_space 에 스페이스 정보를 반환함
    return useQuery(["user", space_uuid], () => userCheck(isValidUUID(space_uuid) ? space_uuid : undefined), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
        retryOnMount: false,
        keepPreviousData: true,
        ...options,
    });
};

export const useSpace = (options = {}) => {
    return useQuery("space", () => getSpace(), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
        keepPreviousData: true,
        ...options,
    });
};

export const useSpaceUpdate = () => {
    const queryClient = useQueryClient();

    return useMutation(patchSpace, {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "user" });
            queryClient.invalidateQueries({ queryKey: "space" });
        },
    });
};

export const useSpaceMember = (space_uuid, params, options = {}) => {
    return useQuery(["spaceMember", space_uuid, params], () => getSpaceMember({ space_uuid, params }), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
        enabled: Boolean(space_uuid),
        ...options,
    });
};

export const useSpaceMemberUpdate = () => {
    const queryClient = useQueryClient();

    return useMutation(patchSpaceMember, {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "user" });
            queryClient.invalidateQueries({ queryKey: "space" });
            queryClient.invalidateQueries({ queryKey: "spaceMember" });
        },
    });
};

export const useSpaceMemberDelete = () => {
    const queryClient = useQueryClient();

    return useMutation(deleteSpaceMember, {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "user" });
            queryClient.invalidateQueries({ queryKey: "space" });
            queryClient.invalidateQueries({ queryKey: "spaceMember" });
        },
    });
};

export const useSpaceMemberInvite = () => {
    const queryClient = useQueryClient();

    return useMutation(postSpaceMemberInvite, {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "user" });
            queryClient.invalidateQueries({ queryKey: "space" });
            queryClient.invalidateQueries({ queryKey: "spaceMember" });
        },
    });
};

export const useSpaceMemberInviteAccept = () => {
    const queryClient = useQueryClient();

    return useMutation(postSpaceMemberInviteAccept, {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "user" });
            queryClient.invalidateQueries({ queryKey: "space" });
            queryClient.invalidateQueries({ queryKey: "spaceMember" });
        },
    });
};

export const useSpaceMemberInviteReject = () => {
    const queryClient = useQueryClient();

    return useMutation(postSpaceMemberInviteReject, {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "user" });
            queryClient.invalidateQueries({ queryKey: "space" });
            queryClient.invalidateQueries({ queryKey: "spaceMember" });
        },
    });
};
